/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { css, names } from 'linaria';

import {
  addAddress2,
  UnitDesignators,
  UnitDesignatorsMap,
  UnitMatchers,
} from '../../utilities/AddressHelper';
import { Store } from '../../redux';
import { Icon, Text } from './core';
import Colors from '../constants/Colors';
import locations from '../../redux/locations';
import type { Place } from '../../data/TripRequests';
import MediaQueries from '../constants/MediaQueries';
import ZIndex from '../constants/ZIndex';
import Spinner from '../components/Spinner';
import { debounce } from '../../utils';

function MissingAddressError({ emptyText }: { emptyText: boolean }) {
  return (
    <div className={MissingAddressErrorStyle}>
      <Icon name="error-chevron" className={MissingAddressErrorChevronStyle} />
      <Text color={Colors.White} size="16" weight="bold">
        {emptyText ? 'Missing address' : 'Can’t find this address'}
      </Text>
      <Text color={Colors.White} size="13" className={MissingAddressErrorBodyTextStyle}>
        Select a suggested address when entering the address
      </Text>
    </div>
  );
}

const MissingAddressErrorStyle = css`
  background-color: ${Colors.RedError};
  border-radius: 10px;
  box-sizing: border-box;
  left: 0;
  margin-top: 8px;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1;
`;
const MissingAddressErrorChevronStyle = css`
  position: absolute;
  left: 50px;
  top: -12px;
`;
const MissingAddressErrorBodyTextStyle = css`
  opacity: 0.7;
`;

type LuggLocationId = string;
type GooglePlaceId = string;

type Suggestion = {
  id: GooglePlaceId,
  title: string,
  description: string,
};

type GoogleSuggestion = {
  place_id: GooglePlaceId,
  description: string,
  types: Array<'street_address' | 'geocode'>,
  matched_substrings: Array<{ offset: number, length: number }>,
  structured_formatting: {
    main_text: string,
    main_text_matched_substrings: Array<{ offset: number, length: number }>,
    secondary_text: string,
    secondary_text_matched_substrings: Array<{ offset: number, length: number }>,
  },
  terms: Array<{ offset: number, value: string }>,
};

class LocationPicker extends React.Component<
  {|
    placeholder: string,
    value: ?string,
    showError: ?boolean,
    showLoading: ?boolean,
    style?: ?Object,
    className?: ?string,
    onChange?: (place: ?Place) => void,
    onChangeAsync?: (place: ?Promise<?Place>) => void,
    onFocus: () => void,
    onBlur: () => void,
    id?: ?LuggLocationId,
    fetchAutocompleteLocationInfo: (
      googleId: GooglePlaceId,
      address2: ?string
    ) => Promise<?{ body: Place, status: number, message?: string }>,
    fetchLocation: (id: LuggLocationId) => Promise<?{ body: Place, status: number }>,
  |},
  {
    text: string,
    address1: string,
    address2: ?string,
    notFound: boolean,
    showDropdown: boolean,
    suggestionsForText: {
      [text: string]: Array<Suggestion> | 'pending',
    },
    currentGoogleId: ?GooglePlaceId,
  }
> {
  state = {
    text: '',
    address1: '',
    address2: null,
    suggestionsForText: {},
    currentGoogleId: null,
    showDropdown: false,
    notFound: false,
  };
  input: HTMLElement | null = null;
  autocompleteService = new google.maps.places.AutocompleteService();
  unmounted = false;

  // Whether to select the next first result that loads. Used when we lose focus while loading.
  selectNext = false;
  // Text to change the field to when selecting the currently-highlighted option.
  activeSuggestionText: string | null = null;

  componentWillMount() {
    if (this.props.value) this.select(this.props.value);
  }

  componentWillReceiveProps({ value }) {
    if (value && value !== this.props.value) this.select(value);
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  async select(id: LuggLocationId) {
    let location = locations.selectors.getLocationById(Store.getState(), id);

    if (!location) {
      await this.props.fetchLocation(id);
      location = locations.selectors.getLocationById(Store.getState(), id);
    }

    if (this.unmounted) return;

    if (location && location.google_place_id !== this.state.currentGoogleId) {
      const { address1 } = this.fetchAddressParts(location.name);
      this.setState({
        text: location.name,
        address1,
        address2: location.address.street2,
        currentGoogleId: location.google_place_id,
      });
    }
  }

  focus = () => {
    if (this.input) this.input.focus();
  };

  blur = () => {
    if (this.input) this.input.blur();
  };

  onFocus = event => {
    if (event.target.value) {
      const { address1, address2 } = this.fetchAddressParts(event.target.value);
      this.setState({ address1, address2 });
      this.fetchSuggestions(address1);
    }
    this.setState({ showDropdown: true });
    if (this.props.onFocus) this.props.onFocus();
  };

  onBlur = () => {
    if (this.state.showDropdown) this.selectCurrent();
    if (this.props.onBlur) this.props.onBlur();
  };

  processSuggestion = ({
    structured_formatting: { main_text: mainText, secondary_text: secondaryText },
    place_id: id,
  }: GoogleSuggestion) => {
    const parts = secondaryText.split(', ');
    return {
      id,
      title: mainText,
      // Remove country and state
      description: [...parts.slice(0, parts.length - 2)].join(', '),
    };
  };

  fetchAddressParts = text => {
    let address1;
    let address2 = null;
    UnitMatchers.some(regexp => {
      address1 = text.replace(regexp, (_, match) => {
        address2 = match
          .split(/\s+/g)
          .map(p => {
            let unit = UnitDesignatorsMap[p];
            if (!unit && UnitDesignators.includes(p)) unit = p;
            return unit ? unit.toUpperCase() : p;
          })
          .join(' ');
        return '';
      });
      return address2 !== null;
    });
    return { address1: address2 ? address1 : text, address2 };
  };

  fetchSuggestions = debounce(text => {
    if (!text) return;
    if (this.state.suggestionsForText[text] && this.state.suggestionsForText[text] !== 'pending') {
      this.onSuggestions(text, this.state.suggestionsForText[text]);
      return;
    }
    this.autocompleteService.getPlacePredictions({ input: text, country: 'us' }, suggestions => {
      if (this.unmounted) return;
      this.onSuggestions(text, (suggestions || []).map(this.processSuggestion));
    });
  }, 200);

  onSuggestions = (text, suggestions) => {
    if (this.state.text !== text) {
      this.setState({
        suggestionsForText: { ...this.state.suggestionsForText, [text]: suggestions },
      });
      return;
    }

    const typingUnitDesignator = UnitDesignators.some(d => this.state.text.trim().endsWith(d));
    this.setState(
      {
        suggestionsForText: { ...this.state.suggestionsForText, [text]: suggestions },
        notFound: suggestions.length === 0 && !typingUnitDesignator,
      },
      () => {
        if (this.selectNext) {
          this.selectNext = false;
          if (suggestions[0]) this.activateAndSelectSuggestion(suggestions[0]);
        } else if (suggestions[0]) {
          this.activateSuggestion(suggestions[0]);
        }
      }
    );
  };

  onChange = event => {
    const text = event.target.value;
    const { address1, address2 } = this.fetchAddressParts(text);
    this.setState({
      text,
      address1,
      address2,
      showDropdown: true,
      notFound: false,
      currentGoogleId: null,
      suggestionsForText: {
        ...this.state.suggestionsForText,
        [address1]: this.state.suggestionsForText[address1] || (address1 ? 'pending' : []),
      },
    });
    this.fetchSuggestions(address1);
  };

  onKeyDown = event => {
    if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
    switch (event.which) {
      case 40: // Down
        event.preventDefault();
        this.activateAdjacent(1);
        break;
      case 38: // Up
        event.preventDefault();
        this.activateAdjacent(-1);
        break;
      case 13: // Enter
        if (this.state.showDropdown) {
          event.preventDefault();
        }
        this.selectCurrent();
        break;
      case 27: // Esc
        this.setState({ showDropdown: false });
        break;
    }
  };

  getSuggestionText = (suggestion: Suggestion) => {
    const { address2 } = this.state;
    const title = suggestion.title;
    return [title + addAddress2(address2), suggestion.description].join(', ');
  };

  activateAndSelectSuggestion = (suggestion: Suggestion) => {
    this.activeSuggestionText = this.getSuggestionText(suggestion);
    this.setState({ currentGoogleId: suggestion.id }, () => {
      this.selectCurrent();
    });
  };

  activateSuggestion = (suggestion: Suggestion) => {
    this.activeSuggestionText = this.getSuggestionText(suggestion);
    this.setState({ currentGoogleId: suggestion.id });
  };

  activateAdjacent = (direction: 1 | -1) => {
    const { address1, suggestionsForText, currentGoogleId } = this.state;
    const suggestions = suggestionsForText[address1];
    if (suggestions === 'pending') return;
    const index = suggestions.findIndex(({ id }) => id === currentGoogleId);
    const suggestion = suggestions[index + direction];
    if (suggestion) {
      this.activeSuggestionText = this.getSuggestionText(suggestion);
      this.setState({ currentGoogleId: suggestion.id });
    }
  };

  resolve: ?(place: ?Place) => void = null;
  promise: ?Promise<?Place> = null;

  selectCurrent = async () => {
    const { currentGoogleId, address1, address2 } = this.state;
    if (!currentGoogleId) {
      if (this.state.suggestionsForText[address1] === 'pending') {
        this.selectNext = true;
        if (this.props.onChangeAsync && !this.promise) {
          this.promise = new Promise(r => (this.resolve = r));
          this.props.onChangeAsync(this.promise);
        }
      } else if (this.props.onChange) {
        this.props.onChange(null);
      }
      return;
    }
    if (this.activeSuggestionText) {
      this.setState({ text: this.activeSuggestionText, showDropdown: false });
      this.activeSuggestionText = null;
    } else {
      this.setState({ showDropdown: false });
    }

    if (this.props.onChangeAsync && !this.promise) {
      this.promise = new Promise(r => (this.resolve = r));
      this.props.onChangeAsync(this.promise);
    }

    await this.props.fetchAutocompleteLocationInfo(currentGoogleId, address2);

    if (this.unmounted) return;

    const location = locations.selectors.getLocationByGoogleId(
      Store.getState(),
      currentGoogleId,
      address2
    );

    if (!location) {
      this.setState({ notFound: true });
      if (this.resolve) this.resolve(null);
      if (this.props.onChange) this.props.onChange(null);
      if (this.props.onChangeAsync) this.props.onChangeAsync(null);
      return;
    } else {
      this.setState({ notFound: false });
    }
    if (this.props.onChange) this.props.onChange(location);
    if (this.resolve) this.resolve(location);
    if (this.props.onChangeAsync) this.props.onChangeAsync(null);
    this.promise = null;
    this.resolve = null;
  };

  render() {
    const { placeholder, showError, style, className, showLoading, id } = this.props;
    const {
      address1,
      address2,
      text,
      notFound,
      suggestionsForText,
      currentGoogleId,
      showDropdown,
    } = this.state;
    const suggestions = suggestionsForText[address1];

    return (
      <div style={{ alignSelf: 'stretch', ...style }} className={className}>
        <input
          autoComplete="off"
          type="text"
          placeholder={placeholder}
          value={text}
          className={SuggestsInputStyle}
          ref={el => (this.input = el)}
          onKeyDown={this.onKeyDown}
          onChange={this.onChange}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          id={id}
        />
        {showDropdown && suggestions && suggestions !== 'pending' && suggestions.length > 0 && (
          <ul className={SuggestsStyle}>
            {suggestions.map((suggestion, i) => (
              <SuggestionItem
                key={i}
                suggestion={suggestion}
                address2={address2}
                active={suggestion.id === currentGoogleId}
                onActivate={this.activateSuggestion}
                onSelect={this.activateAndSelectSuggestion}
              />
            ))}
          </ul>
        )}
        {(showError || notFound) && <MissingAddressError emptyText={!text} />}
        {suggestions === 'pending' && showLoading && (
          <Spinner size={16} color="#848794" className={SpinnerStyle} />
        )}
      </div>
    );
  }
}

class SuggestionItem extends React.Component<{
  suggestion: Suggestion,
  address2: ?string,
  active: boolean,
  onActivate: (suggestion: Suggestion) => void,
  onSelect: (suggestion: Suggestion) => void,
}> {
  onClick = event => {
    event.preventDefault();
    this.props.onSelect(this.props.suggestion);
  };

  onMouseEnter = () => {
    this.props.onActivate(this.props.suggestion);
  };

  onMouseDown = event => {
    event.preventDefault();
  };

  onMouseUp = event => {
    event.preventDefault();
  };

  render() {
    const { suggestion, active, address2 } = this.props;

    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    return (
      <li
        className={names(SuggestItemStyle, active && SuggestItemActiveStyle)}
        onClick={this.onClick}
        onMouseEnter={this.onMouseEnter}
        onMouseDown={this.onMouseDown}
        onMouseUp={this.onMouseUp}>
        <div className={SuggestItemLabelStyle}>{suggestion.title + addAddress2(address2)}</div>
        {suggestion.description}
      </li>
    );
  }
}

export default compose(
  connect(
    () => ({}),
    dispatch => ({
      fetchAutocompleteLocationInfo: (googleId, address2) =>
        dispatch(locations.actions.fetchAutocompleteLocationInfo(googleId, address2)),
      fetchLocation: id => dispatch(locations.actions.fetchLocation(id)),
    }),
    null,
    { withRef: true }
  )
)(LocationPicker);

const SpinnerStyle = css`
  background: ${Colors.White};
  position: absolute;
  top: 34px;
  left: 17px;
`;
const SuggestsInputStyle = css`
  appearance: none;
  background-color: #ffffff;
  border: none;
  color: #1f233b;
  font-size: 18px;
  line-height: 1.3;
  outline: none;
  width: 100%;
  padding-left: 0;
  padding-right: 0;
`;
const SuggestsStyle = css`
  left: 0;
  align-items: stretch;
  margin-top: -10px;
  padding-bottom: 6px;
  padding-top: 6px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: ${ZIndex.Highest};
  border-radius: 10px;
  background: ${Colors.White};
  ${MediaQueries.Medium} {
    box-shadow: 0 3px 16px 0 rgba(0, 0, 0, 0.12);
  }
`;
const SuggestItemStyle = css`
  border-top: 1px solid #f7f7f8;
  padding: 10px 16px;
  text-align: left;
  font-size: 14px;
  color: #848794;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  &:first-child {
    border-top: none;
  }
`;
const SuggestItemLabelStyle = css`
  font-size: 16px;
  color: #1f233b;
`;
const SuggestItemActiveStyle = css`
  background-color: #3b4478;
  color: #969ebd;
  .${SuggestItemLabelStyle} {
    color: #ffffff;
  }
`;



// WEBPACK FOOTER //
// ./src/ui/components/LocationPicker.js